/**
 * CSRF Token Management Service
 * Handles getting and setting CSRF tokens from cookies and headers
 */

export class CSRFService {
  private static readonly CSRF_COOKIE_NAME = 'csrf_token';
  private static readonly CSRF_HEADER_NAME = 'X-CSRF-Token';

  /**
   * Get CSRF token from cookie
   */
  static getTokenFromCookie(): string | null {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === this.CSRF_COOKIE_NAME) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  /**
   * Get CSRF token from response headers (when server provides it)
   */
  static getTokenFromHeaders(headers: any): string | null {
    // Handle both fetch Headers object and axios headers object
    if (headers instanceof Headers) {
      return headers.get(this.CSRF_HEADER_NAME) || headers.get(this.CSRF_HEADER_NAME.toLowerCase()) || null;
    }
    // Handle axios headers
    return headers[this.CSRF_HEADER_NAME] || headers[this.CSRF_HEADER_NAME.toLowerCase()] || null;
  }

  /**
   * Get CSRF token, preferring cookie over local storage
   */
  static getToken(): string | null {
    // First try to get from cookie
    let token = this.getTokenFromCookie();
    
    // If not found in cookie, try localStorage as fallback
    if (!token) {
      token = localStorage.getItem('csrf_token');
    }
    
    return token;
  }

  /**
   * Set CSRF token in localStorage (backup storage)
   */
  static setToken(token: string): void {
    if (token) {
      localStorage.setItem('csrf_token', token);
    }
  }

  /**
   * Clear CSRF token from localStorage
   */
  static clearToken(): void {
    localStorage.removeItem('csrf_token');
  }

  /**
   * Get CSRF header object for API requests
   */
  static getCSRFHeaders(): Record<string, string> {
    const token = this.getToken();
    if (token) {
      console.log('CSRF token found for request:', token.substring(0, 20) + '...');
      return {
        [this.CSRF_HEADER_NAME]: token
      };
    }
    console.warn('No CSRF token available for request - attempting to fetch one');
    // Try to fetch a token synchronously if possible
    // This is a fallback for when the initial initialization failed
    this.fetchCSRFToken().then(newToken => {
      if (newToken) {
        console.log('Successfully fetched CSRF token as fallback');
      }
    }).catch(error => {
      console.error('Fallback CSRF token fetch failed:', error);
    });
    return {};
  }

  /**
   * Check if request needs CSRF protection
   */
  static needsCSRFProtection(method: string, url: string): boolean {
    const safeMethods = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];
    
    // Safe methods don't need CSRF
    if (safeMethods.includes(method.toUpperCase())) {
      return false;
    }

    // Excluded paths that don't need CSRF
    const excludedPaths = [
      '/api/auth/token',
      '/api/auth/activate', 
      '/api/auth/reset-password-request',
  '/api/auth/validate-token',
      '/api/auth/refresh',
      '/api/auth/logout',
      '/api/auth/csrf-token',
      '/api/chat/toolbox/chat-stream',
      '/api/chat/stream',
      '/api/chat/ask-stream'
    ];

    // Check if URL matches any excluded path
    return !excludedPaths.some(path => url.includes(path));
  }

  /**
   * Fetch CSRF token from server (for initial setup)
   */
  static async fetchCSRFToken(): Promise<string | null> {
    try {
      console.log('Fetching CSRF token from server...');
      
      // Make a GET request to the CSRF token endpoint
      const response = await fetch('/api/auth/csrf-token', {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        console.error(`Failed to fetch CSRF token: ${response.status} ${response.statusText}`);
        // Try to log the response body for debugging
        try {
          const errorText = await response.text();
          console.error('CSRF token error response:', errorText);
        } catch (e) {
          console.error('Could not read error response');
        }
        return null;
      }

      console.log('CSRF token response received, checking headers and cookies...');
      
      // Check for CSRF token in response headers
      const headerToken = this.getTokenFromHeaders(response.headers);
      if (headerToken) {
        console.log('Found CSRF token in response headers:', headerToken.substring(0, 20) + '...');
        this.setToken(headerToken);
        return headerToken;
      }

      // Wait a moment for cookie to be set, then check cookies
      await new Promise(resolve => setTimeout(resolve, 200));

      // Check if token is now available in cookies
      const cookieToken = this.getTokenFromCookie();
      if (cookieToken) {
        console.log('Found CSRF token in cookie:', cookieToken.substring(0, 20) + '...');
        this.setToken(cookieToken);
        return cookieToken;
      }

      console.warn('No CSRF token found in headers or cookies after server response');
      console.warn('Response headers:', Array.from(response.headers.entries()));
      console.warn('Current cookies:', document.cookie);
      return null;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    }
  }

  /**
   * Initialize CSRF token on app startup
   */
  static async initialize(): Promise<void> {
    console.log('Initializing CSRF service...');
    
    // Try to get existing token from cookie first (most reliable)
    let token = this.getTokenFromCookie();
    
    if (token) {
      console.log('Found existing CSRF token in cookie');
      this.setToken(token); // Store in localStorage as backup
    } else {
      // Try localStorage as fallback
      token = localStorage.getItem('csrf_token');
      if (token) {
        console.log('Found existing CSRF token in localStorage');
      } else {
        console.log('No existing CSRF token found, fetching new one...');
        // If no token, try to fetch one with retry logic
        let attempts = 0;
        const maxAttempts = 3;
        
        while (attempts < maxAttempts && !token) {
          attempts++;
          console.log(`CSRF token fetch attempt ${attempts}/${maxAttempts}`);
          token = await this.fetchCSRFToken();
          
          if (!token && attempts < maxAttempts) {
            // Wait before retrying
            const delay = attempts;
            await new Promise(resolve => setTimeout(resolve, 1000 * delay));
          }
        }
      }
    }

    if (token) {
      console.log('CSRF token initialized successfully');
    } else {
      console.warn('Could not initialize CSRF token after all attempts');
      console.warn('This may cause CSRF protection errors on form submissions');
    }
  }

  /**
   * Force refresh of CSRF token (for error recovery)
   */
  static async refreshToken(): Promise<string | null> {
    console.log('Refreshing CSRF token...');
    this.clearToken(); // Clear existing token
    return await this.fetchCSRFToken();
  }
}

export default CSRFService;
